import type { ArticleFactCheckSummary } from '@/lib/content-doctrine/article-factcheck';
import {
  type PublicationStage,
  publicationReached,
} from '@/lib/content-doctrine/article-publication-stage';
import {
  type ArticleReleaseInput,
  editorialDisabledReason,
  editorialReviewState,
} from '@/lib/content-doctrine/article-release';

/**
 * `n/a` — the stage does not apply to this article. It is neither done nor
 * outstanding: it never counts for or against completion.
 */
export type ProgressSegmentState = 'done' | 'partial' | 'attested' | 'warning' | 'todo' | 'n/a';

export type ArticleProgressStageId =
  | 'written'
  | 'checked'
  | 'fact-checked'
  | 'client-approved'
  | 'handed-off'
  | 'seo-review'
  | 'images'
  | 'published';

export type ArticleApprovalState = 'approved' | 'waiting' | 'changes' | 'stale' | 'not_sent';

export interface ProgressPublication {
  state: PublicationStage;
  contentHash: string | null;
  approvalBasis: string | null;
  attestationReason: string | null;
  scheduledAt: string | null;
  publishedUrl: string | null;
}

export interface ArticleProgressInput {
  hasArticle: boolean;
  release: ArticleReleaseInput | null;
  contentHash: string | null;
  approval: ArticleApprovalState | null;
  publication: ProgressPublication | null;
  /** The stored claim check, with `isCurrent` already resolved against this article's hash. */
  factCheck?: ArticleFactCheckSummary | null;
}

export interface ArticleProgressStage {
  id: ArticleProgressStageId;
  label: string;
  state: ProgressSegmentState;
  detail: string;
  next: string | null;
}

export interface ArticleProgress {
  stages: ArticleProgressStage[];
  doneCount: number;
  total: number;
  complete: boolean;
  nextStep: string | null;
  nextStageId: ArticleProgressStageId | null;
}

export interface StageResult {
  state: ProgressSegmentState;
  detail: string;
  next: string | null;
}

export interface StageDef {
  id: ArticleProgressStageId;
  label: string;
  derive: (input: ArticleProgressInput) => StageResult;
}

const ATTESTATION_DETAIL: Record<string, string> = {
  approved_outside_mora: 'Approved outside Mora (operator attested)',
  client_review_not_required: 'Review not required (operator attested)',
};

function publicationMatchesCurrent(input: ArticleProgressInput): boolean {
  const publication = input.publication;
  if (!publication) return false;
  if (!publication.contentHash || !input.contentHash) return true;
  return publication.contentHash === input.contentHash;
}

function hostOf(url: string | null): string | null {
  if (!url) return null;
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function checkedFact(release: ArticleReleaseInput): string {
  if (!release.webReadiness) return 'Draft checks have not run';
  if (!release.webReadiness.isCurrent) return 'Checks are out of date';
  if (!release.webReadiness.ready) return 'Draft checks are failing';
  if (release.unresolvedEditorialFindings > 0) {
    return `${release.unresolvedEditorialFindings} editorial finding${release.unresolvedEditorialFindings === 1 ? '' : 's'} unresolved`;
  }
  return 'Language findings unresolved';
}

const HANDED_OFF_BEFORE_CHECK = 'n/a — handed off before this check';

/**
 * A stage that sits before "Handed off" can no longer be acted on once
 * a non-cancelled hand-off covers the article as it stands: the check cannot be
 * run on it, so it would read "to do" forever. Such a stage is `n/a`, never `done`.
 * A hand-off of an earlier version leaves the stage open: the article changed
 * since, and re-checking it is the way back to a hand-off of the current version.
 */
function closedByHandoff(input: ArticleProgressInput): boolean {
  return Boolean(input.publication) && publicationMatchesCurrent(input);
}

const WRITTEN: StageDef = {
  id: 'written',
  label: 'Written',
  derive: (input) =>
    input.hasArticle
      ? { state: 'done', detail: 'Article written', next: null }
      : { state: 'todo', detail: 'Not written yet', next: 'Write the article' },
};

const CHECKED: StageDef = {
  id: 'checked',
  label: 'Checked',
  derive: (input) => {
    if (!input.hasArticle) {
      return { state: 'todo', detail: 'Nothing to check yet', next: null };
    }
    if (!input.release) {
      if (closedByHandoff(input)) {
        return { state: 'n/a', detail: HANDED_OFF_BEFORE_CHECK, next: null };
      }
      return { state: 'todo', detail: 'Not checked yet', next: 'Check the article' };
    }
    const review = editorialReviewState(input.release);
    if (review === 'ready') {
      return { state: 'done', detail: 'Checks and editorial review passed', next: null };
    }
    if (review === 'review-limited') {
      return {
        state: 'partial',
        detail: 'Checks pass; editorial review limited',
        next: 'Refresh the editorial review',
      };
    }
    if (closedByHandoff(input) && !input.release.webReadiness?.isCurrent) {
      return { state: 'n/a', detail: HANDED_OFF_BEFORE_CHECK, next: null };
    }
    const reason = (editorialDisabledReason(input.release) ?? 'Resolve the open checks.')
      .replace(/ before handing (it|this) off\.$/, '')
      .replace(/\.$/, '');
    return { state: 'todo', detail: checkedFact(input.release), next: reason };
  },
};

/**
 * Done only when the claim check on file describes THIS version of the article
 * and passed. A current check that needs review (a claim the law does not state,
 * or one that could not be verified) is `partial`; a current check that found
 * the law misstated is a `warning`. A check on an earlier version, or none at
 * all, is `todo` — except
 * for an article already handed off (see `closedByHandoff`), which is `n/a`,
 * and a check that found no section citation, where there was nothing to check.
 */
const FACT_CHECKED: StageDef = {
  id: 'fact-checked',
  label: 'Claims checked against sources',
  derive: (input) => {
    if (!input.hasArticle) {
      return { state: 'todo', detail: 'Nothing to check yet', next: null };
    }
    const check = input.factCheck;
    if (check?.isCurrent && check.status === 'nothing_to_check') {
      return { state: 'n/a', detail: 'Nothing to check', next: null };
    }
    if (check?.isCurrent && check.passed) {
      return { state: 'done', detail: 'Cited law checked against its source', next: null };
    }
    if (check?.isCurrent && check.status === 'needs_review') {
      const open = check.unsupported + check.unverifiable;
      return {
        state: 'partial',
        detail: `${open} claim${open === 1 ? '' : 's'} need${open === 1 ? 's' : ''} review: not stated by the cited law or could not be verified`,
        next: 'Review the claims the law does not state or that could not be verified',
      };
    }
    if (check?.isCurrent) {
      const wrong = check.contradicted + (check.misapplied ?? 0);
      return {
        state: 'warning',
        detail: `${wrong} claim${wrong === 1 ? '' : 's'} misstate${wrong === 1 ? 's' : ''} the cited law`,
        next: 'Fix the claims that misstate the law',
      };
    }
    if (closedByHandoff(input)) {
      return {
        state: 'n/a',
        detail: HANDED_OFF_BEFORE_CHECK,
        next: null,
      };
    }
    if (check) {
      return {
        state: 'todo',
        detail: 'The claim check was for an earlier version',
        next: 'Check the claims against sources again',
      };
    }
    return {
      state: 'todo',
      detail: 'Claims not checked against sources yet',
      next: 'Check the claims against sources',
    };
  },
};

const CLIENT_APPROVED: StageDef = {
  id: 'client-approved',
  label: 'Client approved',
  derive: (input) => {
    const publication = input.publication;
    if (input.approval === 'approved') {
      return { state: 'done', detail: 'Client approved this version', next: null };
    }
    if (publication?.approvalBasis === 'client_decision' && publicationMatchesCurrent(input)) {
      return { state: 'done', detail: 'Client approved this version', next: null };
    }
    if (publication?.approvalBasis === 'operator_attestation' && publicationMatchesCurrent(input)) {
      return {
        state: 'attested',
        detail:
          (publication.attestationReason && ATTESTATION_DETAIL[publication.attestationReason]) ||
          'Approved outside Mora (operator attested)',
        next: null,
      };
    }
    if (input.approval === 'waiting') {
      return { state: 'todo', detail: 'Waiting on the client', next: 'Waiting on the client' };
    }
    if (input.approval === 'changes') {
      return {
        state: 'todo',
        detail: 'The client asked for changes',
        next: 'Address the client’s changes',
      };
    }
    if (input.approval === 'stale' || (publication && !publicationMatchesCurrent(input))) {
      return {
        state: 'todo',
        detail: 'The approval was for an earlier version',
        next: 'Send this version for approval',
      };
    }
    return { state: 'todo', detail: 'Not sent to the client yet', next: 'Send for approval' };
  },
};

const HANDED_OFF: StageDef = {
  id: 'handed-off',
  label: 'Handed off',
  derive: (input) => {
    const publication = input.publication;
    if (!publication) {
      return { state: 'todo', detail: 'Not handed off yet', next: 'Hand off to the publisher' };
    }
    if (!publicationMatchesCurrent(input)) {
      return {
        state: 'warning',
        detail: 'Handed off · earlier version',
        next: 'Hand off the current version',
      };
    }
    return { state: 'done', detail: 'Handed off', next: null };
  },
};

/**
 * The stages after the hand-off. Each is `done` once the article has moved PAST it, `partial` while it is
 * the stage the article is in, and `todo` before that. A hand-off of an earlier version reached none of them
 * for the article as it stands, so they read `todo` and the Handed off stage carries the warning.
 */
function afterHandoffStage(
  id: ArticleProgressStageId,
  label: string,
  stage: PublicationStage,
  working: { detail: string; next: string },
  idle: { detail: string; next: string },
): StageDef {
  return {
    id,
    label,
    derive: (input) => {
      const publication = input.publication;
      if (!publication || !publicationMatchesCurrent(input)) {
        return { state: 'todo', detail: 'Not handed off yet', next: null };
      }
      if (publication.state === stage) {
        return { state: 'partial', detail: working.detail, next: working.next };
      }
      if (publicationReached(publication.state, stage)) {
        return { state: 'done', detail: 'Done', next: null };
      }
      return { state: 'todo', detail: idle.detail, next: idle.next };
    },
  };
}

const SEO_REVIEW = afterHandoffStage(
  'seo-review',
  'SEO and internal check',
  'seo_review',
  { detail: 'In progress', next: 'Finish the SEO and internal check' },
  { detail: 'Not started', next: 'Start the SEO and internal check' },
);

const IMAGES = afterHandoffStage(
  'images',
  'Adding images',
  'images',
  { detail: 'In progress', next: 'Finish adding images' },
  { detail: 'Not started', next: 'Add images' },
);

const PUBLISHED: StageDef = {
  id: 'published',
  label: 'Published',
  derive: (input) => {
    const publication = input.publication;
    if (publication?.state === 'published') {
      const host = hostOf(publication.publishedUrl);
      return { state: 'done', detail: host ?? 'Published', next: null };
    }
    if (publication?.state === 'scheduled' && publicationMatchesCurrent(input)) {
      return {
        state: 'partial',
        detail: publication.scheduledAt
          ? `Scheduled for ${shortDate(publication.scheduledAt)}`
          : 'Scheduled, no date recorded',
        next: 'Record the publication URL',
      };
    }
    return { state: 'todo', detail: 'Not published yet', next: 'Record the publication URL' };
  },
};

export const ARTICLE_PROGRESS_STAGES: readonly StageDef[] = [
  WRITTEN,
  CHECKED,
  FACT_CHECKED,
  CLIENT_APPROVED,
  HANDED_OFF,
  SEO_REVIEW,
  IMAGES,
  PUBLISHED,
];

export function deriveArticleProgress(
  input: ArticleProgressInput,
  stageDefs: readonly StageDef[] = ARTICLE_PROGRESS_STAGES,
): ArticleProgress {
  const stages: ArticleProgressStage[] = stageDefs.map((def) => ({
    id: def.id,
    label: def.label,
    ...def.derive(input),
  }));
  const applicable = stages.filter((stage) => stage.state !== 'n/a');
  const doneCount = applicable.filter(
    (stage) => stage.state === 'done' || stage.state === 'attested',
  ).length;
  const nextStage = applicable.find(
    (stage) => stage.state !== 'done' && stage.state !== 'attested',
  );
  return {
    stages,
    doneCount,
    total: applicable.length,
    complete: applicable.length > 0 && doneCount === applicable.length,
    nextStep: nextStage?.next ?? null,
    nextStageId: nextStage?.id ?? null,
  };
}

const STATE_WORD: Record<ProgressSegmentState, string> = {
  done: 'done',
  partial: 'partly done',
  attested: 'attested by the operator',
  warning: 'needs attention',
  todo: 'to do',
  'n/a': 'not applicable',
};

export function progressStateWord(state: ProgressSegmentState): string {
  return STATE_WORD[state];
}

export function summariseArticleProgress(
  progress: ArticleProgress,
  nextStep: string | null = progress.nextStep,
): string {
  const stages = progress.stages
    .map((stage) => `${stage.label}: ${progressStateWord(stage.state)}`)
    .join('. ');
  const next = nextStep ? ` Next: ${nextStep.replace(/\.$/, '')}.` : '';
  return `${progress.doneCount} of ${progress.total} steps complete. ${stages}.${next}`;
}
